import { FC } from "react";
import { Grid } from "@mui/material";
import { Box, styled, useTheme } from "@mui/system";
import muiIcons from "../../../components/icons/mui-icons";
import Containers from "../../../components/Containers";
import { CardContainer } from "../../../components/CardContainer";
import Breadcrumb from "../../../components/breadcrumb/Breadcrumb";
import HeatmapChart from "../../../components/apexCharts/HeatmapChart";
import {
  H2,
  H3,
  H5,
  H6,
  Paragraph,
  Span,
} from "../../../components/Typography";
import AdvanceAreaChart from "../../../components/apexCharts/AdvanceAreaChart";
import AdvanceLineChart from "../../../components/apexCharts/AdvanceLineChart";
import {
  titleCardData,
  socialPostData,
  titleCard2Data,
} from "./dashboard5-data";

const FollowerBox = styled(Box)(({ theme }) => ({
  display: "flex",
  "& #content": {
    width: "35%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  "& #chart": {
    width: "65%",
  },
  [theme.breakpoints.down("md")]: {
    display: "block",
    "& #content": {
      width: "100%",
    },
    "& #chart": {
      width: "calc(100vw - 100px)",
    },
  },
}));

const Dashboard5: FC = () => {
  const theme = useTheme();
  const { ChartContainer } = Containers();

  return (
    <>
      <Breadcrumb name="Dashboard" subName="Version 5" path="/dashboard/v1" />
      <Grid container spacing={3}>
        {titleCardData.map((data) => {
          const { name, Icon, title, percent } = data;
          return (
            <Grid item xs={12} sm={6} md={3}>
              <CardContainer
                sx={{
                  textAlign: "center",
                }}
              >
                <Icon sx={{ color: "primary.main", fontSize: 40 }} />
                <Box my="5px" mb="10px">
                  <H2>{name}</H2>
                  <H6>{title}</H6>
                </Box>
                <Span
                  sx={{
                    padding: "2px 20px",
                    borderRadius: "25px",
                    background:
                      theme.palette.mode === "light"
                        ? "#EEEEEE"
                        : theme.palette.primary.main,
                  }}
                >
                  {percent}%
                </Span>
              </CardContainer>
            </Grid>
          );
        })}
      </Grid>

      <Grid container spacing={3} my="0px">
        <Grid item xs={12}>
          <H3>Most Recent Social Media</H3>
        </Grid>
        {socialPostData.map((data) => {
          const { title, subTitle, photoUrl } = data;
          const { HeartIcon, CommentIcon } = muiIcons;
          return (
            <Grid item xs={12} sm={6} md={3}>
              <CardContainer sx={{ p: 0, overflow: "hidden" }}>
                <img src={photoUrl} width="100%" alt="" />
                <Box padding=".4rem 1rem .8rem 1rem">
                  <Span>{subTitle}</Span>
                  <H5 mt="10px" mb="25px">
                    {title}
                  </H5>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box display="flex" alignItems="center" color="grey.600">
                      <HeartIcon sx={{ mr: "4px" }} />
                      <Span>2.3K</Span>
                      <CommentIcon
                        sx={{
                          mr: "4px",
                          ml: "15px",
                          mb: "-3px",
                        }}
                      />
                      <Span>900</Span>
                    </Box>
                    <Span sx={{ color: "grey.600" }}>23 Days Ago</Span>
                  </Box>
                </Box>
              </CardContainer>
            </Grid>
          );
        })}

        <Grid item xs={12}>
          <ChartContainer>
            <Box
              pb="15px"
              display="flex"
              justifyContent="space-between"
              borderBottom={`1px solid ${theme.palette.grey[300]}`}
            >
              <H3>Follower Growth</H3>
              <H3>Follower Growth</H3>
            </Box>
            <FollowerBox>
              <Box id="content">
                <H2 color="primary.main">4,829</H2>
                <Paragraph mb="20px" color="grey.main">
                  Gained Followers (last 360 days)
                </Paragraph>
                <Paragraph my="10px">
                  You have a <Span color="secondary.main">20% Growth</Span>{" "}
                  compare to last year
                </Paragraph>
                <Paragraph>
                  You have a reached <Span color="warning.main">10%</Span> of
                  your follower goal
                </Paragraph>
              </Box>
              <Box id="chart">
                <AdvanceLineChart
                  chartData={[
                    {
                      name: "A",
                      data: [
                        40, 80, 20, 90, 30, 80, 40, 90, 130, 120, 135, 145,
                      ],
                    },
                  ]}
                  colors={[theme.palette.primary.main]}
                  categories={[
                    "Jan",
                    "Feb",
                    "Mar",
                    "Apr",
                    "May",
                    "Jun",
                    "Jul",
                    "Aug",
                    "Sep",
                    "Oct",
                    "Nov",
                    "Dec",
                  ]}
                  height={300}
                />
              </Box>
            </FollowerBox>
          </ChartContainer>
        </Grid>

        <Grid item xs={12}>
          <ChartContainer sx={{ p: 0 }}>
            <Box p="1.5rem">
              <H3>Reach Impression History</H3>
            </Box>
            <AdvanceAreaChart
              chartData={[
                {
                  name: "A",
                  data: [20, 90, 20, 90, 20, 90, 20],
                },
                {
                  name: "B",
                  data: [90, 20, 90, 20, 90, 20, 90],
                },
              ]}
              colors={[theme.palette.primary.main, theme.palette.primary[300]]}
              categories={[
                "00.00",
                "01.00",
                "02.00",
                "03.00",
                "04.00",
                "05.00",
                "06.00",
              ]}
              height={320}
            />
          </ChartContainer>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={3}>
            {titleCard2Data.map((data) => {
              const { name, Icon, title, percent } = data;
              return (
                <Grid item xs={12} sm={6} md={3}>
                  <CardContainer
                    sx={{
                      textAlign: "center",
                    }}
                  >
                    <Icon sx={{ color: "primary.main", fontSize: 40 }} />
                    <Box my="5px" mb="10px">
                      <H2>{name}</H2>
                      <H6>{title}</H6>
                    </Box>
                    <Span
                      sx={{
                        padding: "2px 20px",
                        borderRadius: "25px",
                        background:
                          theme.palette.mode === "light"
                            ? "#EEEEEE"
                            : theme.palette.primary.main,
                      }}
                    >
                      {percent} %
                    </Span>
                  </CardContainer>
                </Grid>
              );
            })}
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartContainer sx={{ p: 0, pr: "10px" }}>
            <Box p="1.5rem" pb="0rem">
              <H3>Life History</H3>
            </Box>
            <AdvanceLineChart
              chartData={[
                {
                  name: "A",
                  data: [40, 80, 20, 90, 110, 130, 120, 135, 145],
                },
                {
                  name: "B",
                  data: [40, 100, 70, 110, 90, 120, 140, 155, 160],
                },
              ]}
              colors={[theme.palette.error.main, theme.palette.grey[400]]}
              categories={[
                "Jan",
                "Feb",
                "Mar",
                "Apr",
                "May",
                "Jun",
                "Jul",
                "Aug",
                "Sep",
              ]}
              height={300}
            />
          </ChartContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartContainer sx={{ p: 0, pr: "10px" }}>
            <Box p="1.5rem" pb="0rem">
              <H3>Comment History</H3>
            </Box>
            <AdvanceLineChart
              chartData={[
                {
                  name: "A",
                  data: [40, 80, 20, 90, 90, 130, 120, 135, 145],
                },
                {
                  name: "B",
                  data: [40, 100, 70, 110, 90, 120, 140, 155, 160],
                },
              ]}
              colors={[theme.palette.success.main, theme.palette.grey[400]]}
              categories={[
                "Jan",
                "Feb",
                "Mar",
                "Apr",
                "May",
                "Jun",
                "Jul",
                "Aug",
                "Sep",
              ]}
              height={300}
            />
          </ChartContainer>
        </Grid>

        <Grid item xs={12}>
          <ChartContainer sx={{ p: 0 }}>
            <Box p="1.5rem" pb="0rem">
              <H3>Posting Habits</H3>
            </Box>
            <HeatmapChart
              chartData={[
                {
                  name: "A",
                  data: [40, 80, 20, 90, 30, 80, 40, 90, 130, 120, 135, 145],
                },
                {
                  name: "B",
                  data: [40, 80, 20, 90, 30, 80, 40, 90, 130, 120, 135, 145],
                },
                {
                  name: "C",
                  data: [40, 80, 20, 90, 30, 80, 40, 90, 130, 120, 135, 145],
                },
                {
                  name: "D",
                  data: [40, 80, 20, 90, 30, 80, 40, 90, 130, 120, 135, 145],
                },
                {
                  name: "E",
                  data: [40, 80, 20, 90, 30, 80, 40, 90, 130, 120, 135, 145],
                },
                {
                  name: "F",
                  data: [40, 80, 20, 90, 30, 80, 40, 90, 130, 120, 135, 145],
                },
              ]}
              colors={[
                theme.palette.primary.main,
                theme.palette.primary.light,
                theme.palette.primary[400],
                theme.palette.primary[300],
              ]}
              categories={[
                "Jan",
                "Feb",
                "Mar",
                "Apr",
                "May",
                "Jun",
                "Jul",
                "Aug",
                "Sep",
                "Oct",
                "Nov",
                "Dec",
              ]}
              height={400}
            />
          </ChartContainer>
        </Grid>
      </Grid>
    </>
  );
};

export default Dashboard5;
